/**
 * Onboarding Flow Component
 * Guides new users through the game mechanics
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Briefcase,
  Trophy,
  Zap,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  Rocket
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Trader Titans!',
    subtitle: 'Your fantasy trading journey begins here',
    icon: Rocket,
    color: 'emerald',
    content: (
      <div className="space-y-4">
        <p className="text-slate-300">
          Trader Titans is a fantasy trading game where you build a virtual portfolio 
          and compete against other players for weekly points.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-emerald-400">V$100K</p>
            <p className="text-xs text-slate-400">Starting Budget</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-400">10</p>
            <p className="text-xs text-slate-400">Instruments to Pick</p>
          </div>
        </div>
        <p className="text-slate-400 text-sm">
          No real money involved - just your trading skills!
        </p>
      </div>
    )
  },
  {
    id: 'portfolio',
    title: 'Build Your Portfolio',
    subtitle: 'Pick 10 instruments to compete',
    icon: Briefcase,
    color: 'blue',
    content: (
      <div className="space-y-4">
        <p className="text-slate-300">
          Your portfolio needs exactly <span className="text-blue-400 font-bold">10 instruments</span> from:
        </p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3">
            <Badge className="bg-blue-500/20 text-blue-400">Stocks</Badge>
            <span className="text-slate-400 text-sm">Apple, Tesla, Amazon...</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3">
            <Badge className="bg-orange-500/20 text-orange-400">Crypto</Badge>
            <span className="text-slate-400 text-sm">Bitcoin, Ethereum, Solana...</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3">
            <Badge className="bg-yellow-500/20 text-yellow-400">Commodities</Badge>
            <span className="text-slate-400 text-sm">Gold, Oil, Silver...</span>
          </div>
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-3">
            <Badge className="bg-emerald-500/20 text-emerald-400">ETFs</Badge>
            <span className="text-slate-400 text-sm">S&P 500, NASDAQ, Emerging Markets...</span>
          </div>
        </div>
        <p className="text-slate-400 text-sm">
          Diversify wisely - each instrument's performance affects your score!
        </p>
      </div>
    )
  },
  {
    id: 'scoring',
    title: 'How Scoring Works',
    subtitle: 'Earn points every week',
    icon: Trophy,
    color: 'amber',
    content: (
      <div className="space-y-4">
        <p className="text-slate-300">
          Points are calculated <span className="text-amber-400 font-bold">every week</span> based on 
          how your instruments perform from Monday to Friday:
        </p>
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
              <div>
                <span className="text-slate-300">Price Performance</span>
                <p className="text-xs text-slate-500">Points for each % your instrument gains</p>
              </div>
            </div>
            <span className="text-emerald-400 font-mono text-sm">+10 pts/1%</span>
          </div>
          <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-400" />
              <div>
                <span className="text-slate-300">Stability Bonus</span>
                <p className="text-xs text-slate-500">Reward for low-volatility picks that hold steady</p>
              </div>
            </div>
            <span className="text-blue-400 font-mono text-sm">+5 pts</span>
          </div>
          <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-purple-400" />
              <div>
                <span className="text-slate-300">Recovery Bonus</span>
                <p className="text-xs text-slate-500">Extra points if an instrument rebounds from a dip</p>
              </div>
            </div>
            <span className="text-purple-400 font-mono text-sm">+15 pts</span>
          </div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          <p className="text-amber-400 text-sm font-medium">Your total weekly score = sum of all 10 instruments' points</p>
          <p className="text-slate-400 text-xs mt-1">Scores from all weeks accumulate into your season total, which determines your league ranking.</p>
        </div>
      </div>
    )
  },
  {
    id: 'powerplay',
    title: 'Power Play',
    subtitle: 'Triple your points on one pick',
    icon: Zap,
    color: 'purple',
    content: (
      <div className="space-y-4">
        <p className="text-slate-300">
          Each week, choose <span className="text-purple-400 font-bold">one instrument</span> as 
          your Power Play to earn <span className="text-purple-400 font-bold">3x points</span> on it!
        </p>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Zap className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <p className="text-purple-400 font-semibold">Example: Bitcoin</p>
              <p className="text-slate-400 text-sm">+30 pts → +90 pts with Power Play!</p>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-sm">
          Choose wisely - you can only set Power Play before the trading window closes!
        </p>
      </div>
    )
  },
  {
    id: 'chips',
    title: 'Chips (Power-ups)',
    subtitle: 'One-time season abilities',
    icon: Sparkles,
    color: 'pink',
    content: (
      <div className="space-y-4">
        <p className="text-slate-300">
          Chips are powerful boosts you can use <span className="text-pink-400 font-bold">once per season</span>. 
          Use them at the perfect moment for maximum impact!
        </p>
        <div className="space-y-2">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center gap-3 mb-1">
              <Badge className="bg-amber-500/20 text-amber-400">Wildcard</Badge>
            </div>
            <p className="text-slate-400 text-sm">Make <span className="text-amber-400 font-medium">unlimited instrument swaps</span> in a single week. Normally you're limited to 1 change per day &mdash; the Wildcard lifts all limits for that week.</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center gap-3 mb-1">
              <Badge className="bg-blue-500/20 text-blue-400">2x Power Play</Badge>
            </div>
            <p className="text-slate-400 text-sm">Double your Power Play multiplier from 3x to <span className="text-blue-400 font-medium">6x points</span> on your chosen instrument for one week.</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center gap-3 mb-1">
              <Badge className="bg-emerald-500/20 text-emerald-400">Safety Net</Badge>
            </div>
            <p className="text-slate-400 text-sm">Protect your portfolio &mdash; any instrument that <span className="text-emerald-400 font-medium">drops in value scores 0</span> instead of negative points.</p>
          </div>
        </div>
        <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3">
          <p className="text-pink-300 text-xs font-medium">Each chip can only be used once per season &mdash; choose your timing wisely!</p>
        </div>
      </div>
    )
  },
  {
    id: 'leagues',
    title: 'Compete in Leagues',
    subtitle: 'Challenge friends and climb the ranks',
    icon: Users,
    color: 'cyan',
    content: (
      <div className="space-y-4">
        <p className="text-slate-300">
          Join leagues to compete with friends, classmates, or the global community!
        </p>
        <div className="space-y-2">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-cyan-400 font-semibold">Rookie League</p>
            <p className="text-slate-400 text-sm">All new players start here &mdash; get your feet wet and learn the ropes in a welcoming environment</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-cyan-400 font-semibold">Season League</p>
            <p className="text-slate-400 text-sm">Compete over a full season for top rankings and climb to higher tiers</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-cyan-400 font-semibold">Major League</p>
            <p className="text-slate-400 text-sm">The top tier &mdash; prove you're among the best traders worldwide</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-cyan-400 font-semibold">Mini Leagues</p>
            <p className="text-slate-400 text-sm">Create your own private league with custom invite codes for friends or colleagues</p>
          </div>
        </div>
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
          <p className="text-cyan-300 text-sm font-medium">Trading Rules</p>
          <p className="text-slate-400 text-xs mt-1">You can make <span className="text-cyan-300">1 instrument swap per day</span>, which rolls over to a max of <span className="text-cyan-300">2 per week</span>. Unused swaps carry forward to the next week but never exceed 2.</p>
        </div>
      </div>
    )
  },
  {
    id: 'ready',
    title: "You're Ready!",
    subtitle: 'Start building your portfolio',
    icon: Check,
    color: 'emerald',
    content: (
      <div className="space-y-4 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
          <Check className="h-10 w-10 text-emerald-400" />
        </div>
        <p className="text-slate-300">
          You're all set! Head to your <span className="text-emerald-400 font-bold">Portfolio</span> page 
          to pick your 10 instruments and start competing.
        </p>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
          <p className="text-emerald-400 font-semibold">Pro Tip</p>
          <p className="text-slate-400 text-sm">
            Complete your portfolio before the trading window closes to start earning points!
          </p>
        </div>
        <a href="/how-it-works" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 underline decoration-dotted mt-2">
          Learn how scoring works in detail
        </a>
      </div>
    )
  }
];

const OnboardingFlow = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { token } = useAuth();
  
  const step = ONBOARDING_STEPS[currentStep];
  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      completeOnboarding();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const completeOnboarding = async () => {
    try {
      await fetch(`${API_URL}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to mark onboarding complete:', err);
    }
    onComplete();
  };

  const handleSkip = async () => {
    try {
      await fetch(`${API_URL}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to mark onboarding complete:', err);
    }
    onSkip();
  };

  const Icon = step.icon;
  const colorClasses = {
    emerald: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30',
    blue: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
    amber: 'text-amber-400 bg-amber-500/20 border-amber-500/30',
    purple: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
    pink: 'text-pink-400 bg-pink-500/20 border-pink-500/30',
    cyan: 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30'
  };

  return (
    <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="onboarding-flow">
      <Card className="bg-slate-900 border-slate-800 w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colorClasses[step.color]?.split(' ').slice(1).join(' ')}`}>
              <Icon className={`h-4 w-4 ${colorClasses[step.color]?.split(' ')[0]}`} />
            </div>
            <span className="text-slate-400 text-sm">
              Step {currentStep + 1} of {ONBOARDING_STEPS.length}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-slate-400 hover:text-white"
            onClick={handleSkip}
            data-testid="skip-onboarding-btn"
          >
            <X className="h-4 w-4 mr-1" />
            Skip
          </Button>
        </div>

        {/* Progress */}
        <div className="px-4 pt-4">
          <Progress value={progress} className="h-1" />
        </div>

        {/* Content */}
        <CardContent className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white">{step.title}</h2>
            <p className="text-slate-400 mt-1">{step.subtitle}</p>
          </div>
          
          {step.content}
        </CardContent>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={isFirstStep}
            className="text-slate-400"
            data-testid="onboarding-prev-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {/* Step indicators */}
          <div className="flex gap-1">
            {ONBOARDING_STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentStep ? 'bg-emerald-400' : 
                  idx < currentStep ? 'bg-emerald-400/50' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            className={isLastStep ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-slate-700 hover:bg-slate-600'}
            data-testid="onboarding-next-btn"
          >
            {isLastStep ? (
              <>
                Get Started
                <Rocket className="h-4 w-4 ml-1" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default OnboardingFlow;
